import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatUSD, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Wallet, Banknote, Calendar, ArrowDownRight, ArrowUpRight } from "lucide-react";

function shiftMonth(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function TreasurerPage() {
  const { activeOrgId } = useAuth();
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeOrgId) return;
    setLoading(true);
    api.get(`/organizations/${activeOrgId}/treasurer-dashboard`, { params: { month } })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [activeOrgId, month]);

  const maxBar = useMemo(() => {
    if (!data) return 0;
    return Math.max(1, ...data.trend.map((t) => Math.max(t.income, t.expense)));
  }, [data]);

  if (loading || !data) return <div className="text-muted-foreground">Loading…</div>;

  const coverage = data.expense_coverage_pct;
  const onTrack = data.is_on_track;
  const projectedNet = data.projected_net;
  const remainingExpense = Math.max(0, data.projected_expense - data.expense);
  const remainingIncome = Math.max(0, data.projected_income - data.income);

  return (
    <div className="space-y-5 fade-up">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Treasurer</div>
        <h1 className="font-serif text-3xl tracking-tight mt-1">Financial pulse</h1>
        <p className="text-sm text-muted-foreground mt-1">Where the church stands this month, with what's coming.</p>
      </div>

      <Card className="rounded-2xl border-border shadow-none p-3 flex items-center justify-between gap-2" data-testid="treasurer-month-picker">
        <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="treasurer-prev-month">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="text-center min-w-0">
          <div className="font-serif text-xl truncate" data-testid="treasurer-month-label">{data.month_label}</div>
          {month !== currentMonth() && (
            <button className="text-[11px] text-primary hover:underline mt-0.5" onClick={() => setMonth(currentMonth())} data-testid="treasurer-jump-current">Jump to current month</button>
          )}
        </div>
        <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="treasurer-next-month">
          <ChevronRight className="w-5 h-5" />
        </Button>
      </Card>

      <Card className={`rounded-2xl shadow-none p-5 ${onTrack ? "bg-primary/5 border-primary/20" : "bg-destructive/5 border-destructive/30"}`}>
        <div className="flex items-start gap-3">
          {onTrack
            ? <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            : <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          }
          <div className="min-w-0">
            <div className={`font-medium ${onTrack ? "text-primary" : "text-destructive"}`} data-testid="treasurer-status">
              {onTrack ? "On track this month" : "Shortfall risk this month"}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {coverage != null ? (
                <>This month's income covers <span className="font-semibold text-foreground">{coverage.toFixed(0)}%</span> of projected expenses.</>
              ) : (
                <>No recurring obligations scheduled for this month.</>
              )}
              {data.projected_expense > 0 && (
                <> Need <span className="font-mono tabular-nums text-foreground">{formatUSD(Math.max(0, data.projected_expense - data.income))}</span> more to cover all rent/recurring.</>
              )}
            </p>
          </div>
        </div>
        {data.projected_expense > 0 && (
          <div className="mt-4">
            <div className="h-2.5 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full ${coverage >= 100 ? "bg-primary" : coverage >= 70 ? "bg-amber-500" : "bg-destructive"}`}
                style={{ width: `${Math.min(100, coverage || 0)}%` }}
                data-testid="treasurer-coverage-bar"
              />
            </div>
            <div className="flex items-center justify-between text-[11px] text-muted-foreground mt-1.5">
              <span>Income {formatUSD(data.income)}</span>
              <span>of {formatUSD(data.projected_expense)} projected</span>
            </div>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <Kpi label="Income" value={data.income} icon={ArrowUpRight} accent="text-primary" testid="treasurer-kpi-income" />
        <Kpi label="Expenses" value={data.expense} icon={ArrowDownRight} accent="text-expense" testid="treasurer-kpi-expense" />
        <Kpi label="Net this month" value={data.net} icon={data.net >= 0 ? TrendingUp : TrendingDown} accent={data.net >= 0 ? "text-primary" : "text-destructive"} testid="treasurer-kpi-net" />
        <Kpi label="Cash on hand" value={data.cash_on_hand} icon={Wallet} accent="text-foreground" testid="treasurer-kpi-coh" />
      </div>

      <Card className="rounded-2xl border-border shadow-none p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Projected — month total</div>
            <div className="font-serif text-2xl mt-1">{formatUSD(data.projected_expense)} <span className="text-sm text-muted-foreground">/ obligations</span></div>
          </div>
          <div className={`text-right ${projectedNet >= 0 ? "text-primary" : "text-destructive"}`}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Projected net</div>
            <div className="font-mono text-lg tabular-nums">{formatUSD(projectedNet)}</div>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
          <span>Remaining income expected: <span className="font-mono tabular-nums text-foreground">{formatUSD(remainingIncome)}</span></span>
          <span>Remaining bills: <span className="font-mono tabular-nums text-foreground">{formatUSD(remainingExpense)}</span></span>
        </div>
      </Card>

      <Card className="rounded-2xl border-border shadow-none overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <div className="text-sm font-semibold">Scheduled obligations · {data.obligations.length}</div>
        </div>
        {data.obligations.length === 0 ? (
          <div className="p-5 text-sm text-muted-foreground">No recurring rules due this month.</div>
        ) : (
          <div className="divide-y divide-border">
            {data.obligations.map((o) => (
              <div key={o.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`obligation-${o.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate flex items-center gap-2">
                    <Badge variant="outline" className={`rounded-lg text-[10px] ${o.type === "income" ? "bg-primary/10 text-primary border-primary/20" : "bg-expense/10 text-expense border-expense/20"}`}>{o.type}</Badge>
                    {o.name}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 truncate">
                    {o.category_name || "—"} · {o.frequency}{o.occurrences > 1 ? ` × ${o.occurrences}` : ""} · next {formatDate(o.next_run_date)}
                  </div>
                </div>
                <div className={`font-mono tabular-nums ${o.type === "income" ? "text-primary" : "text-expense"}`}>{formatUSD(o.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="rounded-2xl border-border shadow-none p-5">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-muted-foreground" /> 6-month trend</div>
          <div className="text-[10px] text-muted-foreground">Income vs Expense</div>
        </div>
        <div className="mt-4 grid grid-cols-6 gap-1.5 items-end h-32">
          {data.trend.map((t, i) => {
            const inH = (t.income / maxBar) * 100;
            const exH = (t.expense / maxBar) * 100;
            return (
              <div key={i} className="flex flex-col items-center gap-1">
                <div className="w-full flex gap-0.5 items-end h-24">
                  <div className="flex-1 bg-primary/80 rounded-t" style={{ height: `${inH}%` }} title={`Income ${formatUSD(t.income)}`}></div>
                  <div className="flex-1 bg-expense/80 rounded-t" style={{ height: `${exH}%` }} title={`Expense ${formatUSD(t.expense)}`}></div>
                </div>
                <div className="text-[10px] text-muted-foreground">{t.label}</div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-4 text-[10px] text-muted-foreground mt-2">
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-primary/80 rounded-sm" />Income</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-expense/80 rounded-sm" />Expense</span>
        </div>
      </Card>

      {data.top_expense_categories.length > 0 && (
        <Card className="rounded-2xl border-border shadow-none overflow-hidden">
          <div className="px-5 py-3 border-b border-border text-sm font-semibold">Top expense categories</div>
          <div className="divide-y divide-border">
            {data.top_expense_categories.map((c, i) => (
              <div key={i} className="px-5 py-2.5 flex items-center justify-between">
                <span className="text-sm truncate">{c.category}</span>
                <span className="font-mono tabular-nums text-sm text-expense">{formatUSD(c.total)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="rounded-2xl border-border shadow-none overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center gap-2">
          <Banknote className="w-4 h-4 text-muted-foreground" />
          <div className="text-sm font-semibold">This month's transactions · {data.recent_transactions.length}</div>
        </div>
        {data.recent_transactions.length === 0 ? (
          <div className="p-5 text-sm text-muted-foreground">No transactions yet this month.</div>
        ) : (
          <div className="divide-y divide-border">
            {data.recent_transactions.map((t) => (
              <div key={t.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`treasurer-txn-${t.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{t.description || t.category_name || (t.type === "income" ? "Income" : "Expense")}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                    {formatDate(t.date)} · {t.category_name}
                    {t.fund_name && ` · ${t.fund_name}`}
                    {t.vendor_name && ` · ${t.vendor_name}`}
                    {t.donor_name && ` · ${t.donor_name}`}
                  </div>
                </div>
                <div className={`font-mono text-sm tabular-nums shrink-0 ${t.type === "income" ? "text-primary" : "text-expense"}`}>
                  {t.type === "income" ? "+" : "-"}{formatUSD(t.amount)}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="rounded-2xl border-border shadow-none p-5 bg-secondary/30">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Year to date · {data.month.slice(0, 4)}</div>
        <div className="grid grid-cols-3 gap-3 mt-2">
          <div>
            <div className="text-[10px] text-muted-foreground">Income</div>
            <div className="font-mono tabular-nums text-primary">{formatUSD(data.ytd_income)}</div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground">Expense</div>
            <div className="font-mono tabular-nums text-expense">{formatUSD(data.ytd_expense)}</div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground">Net</div>
            <div className={`font-mono tabular-nums ${data.ytd_net >= 0 ? "text-primary" : "text-destructive"}`}>{formatUSD(data.ytd_net)}</div>
          </div>
        </div>
        {data.runway_months != null && (
          <div className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
            At current projected expenses, available net covers{" "}
            <span className="font-semibold text-foreground">
              {data.runway_months > 0 ? `${data.runway_months.toFixed(1)} months` : "0 months"}
            </span> of obligations.
          </div>
        )}
      </Card>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent, testid }) {
  return (
    <Card className="rounded-2xl border-border shadow-none p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <Icon className={`w-4 h-4 ${accent}`} />
      </div>
      <div className={`font-mono text-xl tabular-nums mt-1.5 ${accent}`}>{formatUSD(value)}</div>
    </Card>
  );
}
